import type { ReactNode } from "react";

const TONES = {
  gold: {
    idle: "border-[#efdcc0] bg-[#fffaf3] hover:border-[#e2c393]",
    selected: "border-[#d5a253] bg-[#fff6e8] ring-1 ring-[#d5a253]",
  },
  rose: {
    idle: "border-[#f0d4d4] bg-[#fdf4f4] hover:border-[#e6b8b8]",
    selected: "border-[#d98b8b] bg-[#fcecec] ring-1 ring-[#d98b8b]",
  },
} as const;

interface CheckoutAddonCardProps {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean;
  readonly tone: keyof typeof TONES;
  readonly icon: ReactNode;
  readonly title: ReactNode;
  readonly optionalLabel: string;
  readonly price: string;
  /** Content inside the label: clicking it toggles the add-on. */
  readonly children?: ReactNode;
  /** Content below the label, such as form fields. */
  readonly after?: ReactNode;
}

/** Optional add-on of the payment step: checkbox, title, "optional" badge and price. */
export function CheckoutAddonCard({
  checked,
  onChange,
  disabled = false,
  tone,
  icon,
  title,
  optionalLabel,
  price,
  children,
  after,
}: CheckoutAddonCardProps) {
  return (
    <div
      className={`rounded-2xl border p-3.5 transition-colors sm:p-5 ${
        checked ? TONES[tone].selected : TONES[tone].idle
      }`}
    >
      <label className="flex cursor-pointer items-start gap-2.5 sm:gap-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          disabled={disabled}
          className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-emerald-600 sm:mt-1"
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-start justify-between gap-3">
            {/* The badge stays beside the title and only wraps on very narrow phones. */}
            <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 sm:gap-x-2">
              <span className="flex shrink-0">{icon}</span>
              <span className="whitespace-nowrap font-title text-base leading-tight text-[#5c1616] sm:text-xl">
                {title}
              </span>
              <span className="shrink-0 rounded-full border border-[#e8d6cc] bg-white/80 px-1.5 py-px text-[10px] text-[#8a6a5f] sm:px-2 sm:py-0.5 sm:text-[11px]">
                {optionalLabel}
              </span>
            </span>
            <span className="shrink-0 whitespace-nowrap font-title text-base text-[#5c1616] sm:text-lg">
              + {price}
            </span>
          </span>
          {children}
        </span>
      </label>
      {after}
    </div>
  );
}
